import { format, isAfter, isBefore, parseISO } from "date-fns";
import {
  Calendar,
  Clock,
  Eye,
  EyeOff,
  MoreHorizontal,
  BookOpen,
  Users,
  CheckCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { DropdownMenuSeparator } from "src/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { useLanguage } from "../../../../context/LanguageContext";
import { cn } from "../../../../lib/utils";
import { QuizData } from "../../../../lib/languages";

// Utility for parsing multilingual fields
const parseField = (field, language) => {
  if (!field) return "";
  try {
    const parsed = JSON.parse(field.en);
    return parsed[language] || parsed.en || "";
  } catch {
    return field[language] || field?.en || "";
  }
};

// Formats durations like "HH:MM:SS" to human-readable
const formatDuration = (duration) => {
  const [h = 0, m = 0] = duration.split(":").map(Number);
  if (h > 0 && m > 0) return `${h} hr ${m} min`;
  if (h > 0) return `${h} hr`;
  if (m > 0) return `${m} min`;
  return duration;
};

// Formats date to readable format
const formatDate = (dateString) => {
  try {
    const iso = dateString.replace(" ", "T");
    return format(parseISO(iso), "MMM d, h:mm a");
  } catch {
    return dateString;
  }
};

export default function QuizCard({ quiz, onStart }) {
  const { language } = useLanguage();
  const t = QuizData[language] || QuizData.en;

  const title = parseField(quiz?.title, language);
  const description = parseField(quiz?.description, language);
  const lessonTitle = quiz.lesson
    ? parseField(quiz.lesson?.title, language)
    : "";
  const lessonDescription = quiz.lesson
    ? parseField(quiz.lesson?.description, language)
    : "";

  const now = new Date();
  const start = parseISO(quiz.start_date.replace(" ", "T"));
  const end = parseISO(quiz.end_date.replace(" ", "T"));
  const isNotStarted = isBefore(now, start);
  const isExpired = isAfter(now, end);
  const isAvailable = !isNotStarted && !isExpired;

  let buttonText;
  let buttonDisabled = false;

  if (isNotStarted) {
    buttonText = t.notStarted;
    buttonDisabled = true;
  } else if (isExpired && !quiz.has_attempted) {
    buttonText = t.expired;
    buttonDisabled = true;
  } else if (
    (isExpired && quiz.has_attempted) ||
    (isAvailable && quiz.has_attempted && quiz.show_answer)
  ) {
    buttonText = t.showAnswers;
  } else if (isAvailable && quiz.has_attempted) {
    buttonText = t.attempted;
    buttonDisabled = true;
  } else {
    buttonText = t.startQuiz;
  }

  console.log(quiz.has_attempt)
  return (
    <Card className="w-full transition-all duration-300 hover:shadow-lg relative">
      {quiz.has_attempted && (
        <div className="absolute -top-2 -start-2 z-10">
          <Badge className="bg-green-600 text-white border-0 shadow-md flex items-center gap-1 px-2 py-1 rounded-full">
            <CheckCircle className="h-3 w-3" />
            {t.attempted}
          </Badge>
        </div>
      )}

      <CardHeader className="px-4 py-3 bg-primary/5 rounded-t-lg">
        <div className="flex justify-between items-center gap-2">
          <CardTitle className="text-xl font-bold truncate flex-1">
            {title}
          </CardTitle>
          {quiz.quiz_attempts?.length > 0 && !!quiz.show_degree && (
            <Badge className="bg-primary text-primary-foreground">
              {Number.parseInt(
                quiz.quiz_attempts[quiz.quiz_attempts.length - 1].score
              )}{" "}
              / {quiz.total_score}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
          <Clock className="h-4 w-4" />
          {formatDuration(quiz.duration)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 text-sm text-muted-foreground mb-2">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>
              {t.start}: {formatDate(quiz.start_date)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>
              {t.end}: {formatDate(quiz.end_date)}
            </span>
          </div>
        </div>

        {quiz.lesson && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <BookOpen className="h-4 w-4" />
            <span className="truncate flex-1">
              {t.lesson}: {lessonTitle}
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between p-4">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent
            className="sm:max-w-md"
            dir={language === "ar" ? "rtl" : "ltr"}
          >
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <div
                className="text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </DialogHeader>

            <div className="grid gap-4">
              <div className="flex justify-between w-full text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  {quiz.show_degree ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                  <span>
                    {quiz.show_degree ? t.gradesVisible : t.gradesHidden}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {quiz.show_answer ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                  <span>
                    {quiz.show_answer ? t.answersVisible : t.answersHidden}
                  </span>
                </div>
              </div>

              {quiz.lesson && (
                <>
                  <DropdownMenuSeparator />
                  <div className="grid gap-2">
                    <h3 className="font-medium">{t.lessonInfo}</h3>
                    <div className="text-sm">
                      <p className="font-medium">{lessonTitle}</p>
                      <p
                        className="text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: lessonDescription }}
                      />
                    </div>

                    {quiz.lesson.enroll_course && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {t.course}: {quiz.lesson.enroll_course.name}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Button
          className={cn(
            "transition-transform duration-300",
            buttonDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : buttonText === t.showAnswers
              ? "bg-blue-600 hover:bg-blue-500 text-white"
              : "bg-green-600"
          )}
          disabled={buttonDisabled}
          onClick={onStart}
        >
          {buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
}
